import { TableLayout, type LayoutInfo, type Rect } from 'react-aria-components/Virtualizer';

/** The `protected` members of react-aria's `TableLayout` that `createDataGridLayout` reaches. */
interface TableLayoutInternals {
  buildLoader: (...args: unknown[]) => { layoutInfo: LayoutInfo; validRect: Rect };
  requestedRect: Rect;
}

/**
 * Idle, react-aria lays the load-more row out at 0px inside wrappers that clip, so its 1px sentinel
 * only intersects by touching an edge — which sub-pixel layout can miss, stalling infinite scroll.
 * One pixel of height keeps the sentinel inside every clip.
 *
 * It patches an instance rather than subclassing: the app compiles to ES5, and an ES5 class cannot
 * extend react-aria's native one ("Class constructor … cannot be invoked without 'new'").
 *
 * It relies on react-aria internals, so react-aria-components is pinned to an exact version and
 * `src/tests/pricelists/ui/DataGrid/createDataGridLayout.test.ts` fails if an upgrade changes them.
 */
export const createDataGridLayout = (): TableLayout<unknown> => {
  const layout = new TableLayout<unknown>();
  const internals = layout as unknown as TableLayoutInternals;
  const buildLoader = internals.buildLoader.bind(layout);

  internals.buildLoader = (...args: unknown[]) => {
    const loader = buildLoader(...args);

    if (loader.layoutInfo.rect.height === 0) {
      loader.layoutInfo.rect.height = 1;
      loader.validRect = loader.layoutInfo.rect.intersection(internals.requestedRect);
    }

    return loader;
  };

  return layout;
};
